import { Dictionary } from 'underscore';

import { getCookie } from './TagUtils';

type ParamDict = Dictionary<string | number>;
// Helper modules to populate headers and other fields of fetch requests
export namespace TAG {
  export interface TAGError {
    httpErrorCode: number;
    message: string;
    tableauErrorCode: number;
  }

  const checkForErrors = async (response: Response): Promise<void> => {
    if (!response.ok) {
      const error: TAGError = await response.json();
      throw error;
    }
  };

  // Base request for all TAG apis.
  function getBaseRequest() {
    return {
      headers: {
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN') || '',
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      cache: 'no-cache' as 'no-cache',
      credentials: 'include' as 'include',
    };
  }

  const baseUrl = `${window.location.origin}/services/`;

  const getFullUrl = (url: string, searchParams: ParamDict = {}): string => {
    const combinedUrl = new URL(baseUrl + url);
    Object.keys(searchParams)
      .forEach(key => {
        if (searchParams[key] === undefined) { return; }
        combinedUrl.searchParams.append(key, `${searchParams[key]}`);
      });
    return combinedUrl.toString();
  };

  /**
   * GET gets the resource at that location.
   * Returns a json blob form of that object if successful
   */
  export const Get = async <T>(url: string, searchParams?: ParamDict): Promise<T> => {
    const fullUrl = getFullUrl(url, searchParams);
    const baseRequest = getBaseRequest();
    const response = await fetch(fullUrl, { ...baseRequest, method: 'GET' });
    await checkForErrors(response);
    const value = await response.json();
    return value as T;
  };

  /**
   * DELETE deletes the resource at that location.
   * Returns nothing if successful
   */
  export const Delete = async (url: string): Promise<void> => {
    const fullUrl = getFullUrl(url);
    const baseRequest = getBaseRequest();
    const response = await fetch(fullUrl, { ...baseRequest, method: 'DELETE' });
    await checkForErrors(response);
  };

  /**
   * PATCH patches the resource at that location.
   * Accepts a diff object
   * Returns the full updated object if successful
   */
  export const Patch = async <T>(url: string, diff: Partial<T>): Promise<T> => {
    const fullUrl = getFullUrl(url);
    const baseRequest = getBaseRequest();
    const response = await fetch(fullUrl, {
      ...baseRequest,
      method: 'PATCH',
      body: JSON.stringify(diff),
    });
    await checkForErrors(response);
    const value = await response.json();
    return value as T;
  };

  /**
   * PUT sets the resource at that location to a given value
   * Accepts the new value of that resource, of type T
   * Often returns the sent object of type T when succesful
   * but may instead return object of type R (depending on the request)
   */
  export const Put = async <T, R>(url: string, body: T): Promise<T | R> => {
    const fullUrl = getFullUrl(url);
    const baseRequest = getBaseRequest();
    const response = await fetch(fullUrl, {
      ...baseRequest,
      method: 'PUT',
      body: JSON.stringify(body),
    });
    await checkForErrors(response);
    const value = await response.json();
    return value as T | R;
  };

  /**
   * POST does some arbitrary action in the backend.
   * Accepts a body of type T
   * Returns an object of type R if successful
   */
  export const Post = async <T, R>(url: string, body: T): Promise<R> => {
    const fullUrl = getFullUrl(url);
    const baseRequest = getBaseRequest();
    const response = await fetch(fullUrl, {
      ...baseRequest,
      method: 'POST',
      body: JSON.stringify(body),
    });
    await checkForErrors(response);
    const value = await response.json();
    return value as R;
  };
}
